import { BrandLogo } from '@brightdrop/bd-ui';
import { LocalizationProvider } from '@brightdrop/localization-core';
import { Box, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, Outlet } from 'react-router-dom';
import LocaleSwitcher from '~/common/components/LocaleSwitcher/LocaleSwitcher';
import {
  GM_COMPANY_NAME,
  HOMEPAGE_URL,
} from '~/common/constants/company.constant';
import { useOnStarSupportEmail } from '~/common/hooks/useOnStarFleetIntelligenceSupportEmail';
import { AppRoutePaths } from '~/common/models/route.model';
import {
  selectProfileEffectiveUserPreferences,
  selectProfileUserPreferences,
} from '~/store/profile/profileSlice.selectors';

import TermsAndConditionsModal from '../components/terms-conditions/Modal';
import useStyles from './QuickpayLayout.styles';
import useQuickPayLocale from './useQuickPayLocale';

const TRANSLATION_KEYS = {
  termsAndConditions: 'common:termsConditions.title',
  contact: 'common:support.subTitle',
  contactSupportAriaLabel: 'common:contactSupportAriaLabel',
  homepageAriaLabel: 'common:homepageAriaLabel',
} as const;

const QuickpayLayout = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const supportEmail = useOnStarSupportEmail();
  const { showSelector, locale, onLocaleChange } = useQuickPayLocale();
  const userPreferences = useSelector(selectProfileUserPreferences);
  const effectiveUserPreferences = useSelector(
    selectProfileEffectiveUserPreferences
  );

  /**
   * Preferences for the nested `LocalizationProvider`. A signed-out visitor has
   * no profile, so the chosen locale is the only preference there is. Anyone
   * else is handed the same payload the root provider resolves, so the scope is
   * a no-op for them. The provider stays mounted either way, so the route under
   * it is never remounted when the selector appears or disappears.
   */
  const scopedPreferences = useMemo(
    () =>
      showSelector
        ? { userPreferences: { locale } }
        : { userPreferences, effectiveUserPreferences },
    [showSelector, locale, userPreferences, effectiveUserPreferences]
  );

  // Route guard: Redirect if Quick Pay is disabled
  if (!globalThis.appConfig.quickPayEnabled) {
    return <Navigate to={AppRoutePaths.PAGE_NOT_FOUND} replace />;
  }

  // TODO: add loading state
  return (
    <div className={classes.layout} data-testid="quick-pay-layout">
      <header className={classes.header}>
        <div className={classes.headerContent}>
          <a
            href={HOMEPAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t(TRANSLATION_KEYS.homepageAriaLabel)}
          >
            <BrandLogo
              width={231}
              height={32}
              aria-hidden
              data-testid="quickpay-header-logo"
            />
          </a>
          {showSelector && (
            <Box className={classes.localeSelect}>
              <LocaleSwitcher
                value={locale}
                onChange={onLocaleChange}
                testId="quickpay-locale-select"
                size="small"
              />
            </Box>
          )}
        </div>
      </header>

      <div className={classes.body}>
        <div className={classes.bodyContent}>
          {/* Scopes formatting to the chosen locale only; the request locale
              stays with the root provider. */}
          <LocalizationProvider
            preferences={scopedPreferences}
            enabled={undefined}
          >
            <Outlet />
          </LocalizationProvider>
          <button
            type="button"
            className={classes.termsAndConditions}
            onClick={() => setIsTermsModalOpen(true)}
          >
            <Typography variant="small_body_regular">
              {t(TRANSLATION_KEYS.termsAndConditions)}
            </Typography>
          </button>
          <TermsAndConditionsModal
            open={isTermsModalOpen}
            onClose={() => setIsTermsModalOpen(false)}
          />
        </div>
      </div>

      <footer data-testid="quick-pay-footer" className={classes.footer}>
        <div className={classes.footerContent}>
          <Typography
            component="div"
            variant="caption_regular"
            className={classes.footerLinks}
          >
            <span>&copy; {GM_COMPANY_NAME}</span>
            <a
              href={`mailto:${supportEmail}`}
              aria-label={t(TRANSLATION_KEYS.contactSupportAriaLabel)}
            >
              {t(TRANSLATION_KEYS.contact)}
            </a>
          </Typography>
        </div>
      </footer>
    </div>
  );
};

export default QuickpayLayout;
